/**
 * US-11 asset-library picker on the Content Tracker item page. Lists the ready files of the client workspace's asset
 * library and attaches one as the next version (v1, v2, ...); with the agency's write access it can also upload a file
 * into that library. Access, versioning and file checks are enforced on the server; this screen renders and sends.
 * Kept out of content.tsx so the item page only mounts it.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { Button, Text } from '@qm/ui';
import { useSurface } from '../kit/context';
import { errorInfo } from '../kit/http';
import { useAction, useResource } from '../kit/resource';
import { Badge, Field, Loadable, Notice, errorMessage } from '../kit/ui';
import {
  contentApi, LIBRARY_UPLOAD_MAX_BYTES, LIBRARY_UPLOAD_TYPES, uploadToLibrary,
  type LibraryAsset, type LibraryUploadResult, type LibraryView,
} from '../api/content';

export const libraryKey = (itemId: string, q: string) => `content:library:${itemId}:${q}`;

/** Server reasons for a refused upload that have their own line; anything else gets the generic message. */
const UPLOAD_REASONS = ['unsupported_type', 'too_large', 'content_mismatch', 'executable', 'unknown_content', 'malware_detected', 'empty'] as const;
/** The reason a validation error carries in details[0].message (the library's reject codes), or null. */
export function uploadRejectReason(err: unknown): string | null {
  if (errorInfo(err).code !== 'validation_failed') return null;
  const details = (err as { envelope?: { error?: { details?: unknown } } }).envelope?.error?.details;
  const first = Array.isArray(details) ? (details[0] as { message?: unknown } | undefined) : undefined;
  const msg = typeof first?.message === 'string' ? first.message : null;
  return msg && (UPLOAD_REASONS as readonly string[]).includes(msg) ? msg : null;
}

/** A file the browser picked that the library will refuse anyway: checked first so nothing is sent. */
export function localUploadProblem(file: { type: string; size: number }): 'unsupported_type' | 'too_large' | 'empty' | null {
  if (!(LIBRARY_UPLOAD_TYPES as readonly string[]).includes(file.type)) return 'unsupported_type';
  if (file.size === 0) return 'empty';
  if (file.size > LIBRARY_UPLOAD_MAX_BYTES) return 'too_large';
  return null;
}

export function fmtSize(locale: string, bytes: number): string {
  const tag = locale === 'ar' ? 'ar-AE-u-nu-latn' : 'en-AE';
  const nf = (n: number) => new Intl.NumberFormat(tag, { maximumFractionDigits: 1 }).format(n);
  if (bytes >= 1024 * 1024) return `${nf(bytes / (1024 * 1024))} MB`;
  return `${nf(Math.max(1, Math.round(bytes / 1024)))} KB`;
}

/** Collapsed by default: the library is read only when the user opens the picker. */
export function LibraryPicker({ itemId, onDone }: { itemId: string; onDone: () => void }) {
  const { t } = useSurface();
  const [open, setOpen] = useState(false);
  return (
    <details className="qc-library" onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary>{t('portal.partners.content.library.open')}</summary>
      {open ? <LibraryBrowser itemId={itemId} onDone={onDone} /> : null}
    </details>
  );
}

export function LibraryBrowser({ itemId, onDone }: { itemId: string; onDone: () => void }) {
  const { t, http } = useSurface();
  const api = useMemo(() => contentApi(http), [http]);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const res = useResource<LibraryView>(libraryKey(itemId, search), () => api.library(itemId, search ? { q: search } : undefined));
  const submit = (e: FormEvent) => { e.preventDefault(); setSearch(q.trim()); };
  return (
    <div className="qc-library-body" aria-live="polite">
      <Loadable resource={res}>
        {(lib) => !lib.available ? (
          <Text tone="muted">{t(lib.reason === 'no_workspace' ? 'portal.partners.content.library.noWorkspace' : 'portal.partners.content.library.noAccess')}</Text>
        ) : (
          <>
            <form role="search" onSubmit={submit} className="qc-form-grid" aria-label={t('portal.partners.content.library.search')}>
              <Field label={t('portal.partners.content.library.search')}>
                {(id) => <input id={id} type="search" name="librarySearch" maxLength={100} value={q} onChange={(e) => setQ(e.target.value)} />}
              </Field>
              <div><Button type="submit">{t('portal.partners.content.library.searchButton')}</Button></div>
            </form>
            {lib.assets.length === 0 ? <Text tone="muted">{t('portal.partners.content.library.empty')}</Text> : (
              <ul className="qc-list" data-testid="library-assets">
                {lib.assets.map((a) => <LibraryRow key={`${a.id}:${a.version}`} itemId={itemId} asset={a} onDone={() => { res.reload(); onDone(); }} />)}
              </ul>
            )}
            {lib.canUpload ? <LibraryUpload itemId={itemId} onDone={() => { res.reload(); onDone(); }} /> : null}
          </>
        )}
      </Loadable>
    </div>
  );
}

function LibraryRow({ itemId, asset, onDone }: { itemId: string; asset: LibraryAsset; onDone: () => void }) {
  const { t, locale, http } = useSurface();
  const api = useMemo(() => contentApi(http), [http]);
  const act = useAction(() => api.attachAsset(itemId, { assetId: asset.id, version: asset.version }));
  return (
    <li>
      <strong>{asset.title}</strong>{' '}
      <Badge>{t(`portal.partners.content.library.kind.${asset.kind}`)}</Badge>{' '}
      <small className="qc-hint">{asset.filename} · {t('portal.partners.content.item.version', { n: asset.version })} · {fmtSize(locale, asset.sizeBytes)}</small>{' '}
      <Button onClick={async () => { if (await act.run()) onDone(); }} disabled={act.pending}
        aria-label={t('portal.partners.content.library.attachNamed', { title: asset.title })}>
        {t('portal.partners.content.library.attach')}
      </Button>
      {act.error ? <div className="qx-alert" role="alert">{errorMessage(t, act.error)}</div> : null}
    </li>
  );
}

function LibraryUpload({ itemId, onDone }: { itemId: string; onDone: () => void }) {
  const { t } = useSurface();
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');
  const act = useAction((f: File, l: string) => uploadToLibrary(itemId, f, l ? { label: l } : {}));
  const [done, setDone] = useState<LibraryUploadResult['status'] | null>(null);
  const local = file ? localUploadProblem(file) : null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file || local) return;
    setDone(null);
    const r = await act.run(file, label.trim());
    if (r) { setDone(r.status); setFile(null); setLabel(''); onDone(); }
  };
  const reason = act.error ? uploadRejectReason(act.error) : null;
  return (
    <form onSubmit={submit} className="qc-form-grid" aria-label={t('portal.partners.content.library.upload')}>
      <Field label={t('portal.partners.content.library.uploadFile')} hint={t('portal.partners.content.library.uploadHint')}
        error={local ? t(`portal.partners.content.library.reject.${local}`) : null}>
        {(id, desc) => <input id={id} type="file" name="libraryFile" accept={LIBRARY_UPLOAD_TYPES.join(',')} aria-describedby={desc}
          onChange={(e) => { setDone(null); setFile(e.target.files?.[0] ?? null); }} />}
      </Field>
      <Field label={t('portal.partners.content.library.label')}>
        {(id) => <input id={id} name="libraryLabel" maxLength={120} value={label} onChange={(e) => setLabel(e.target.value)} />}
      </Field>
      <div><Button type="submit" disabled={!file || !!local || act.pending}>{t('portal.partners.content.library.upload')}</Button></div>
      {act.error ? <div className="qx-alert" role="alert">{reason ? t(`portal.partners.content.library.reject.${reason}`) : errorMessage(t, act.error)}</div> : null}
      {done === 'scanning' ? <Notice tone="warn">{t('portal.partners.content.library.scanning')}</Notice> : null}
      {done === 'attached' ? <Notice tone="good">{t('portal.partners.content.library.attached')}</Notice> : null}
    </form>
  );
}
